import { FindingsInsightsDto, Page } from '@ampsec/platform-client';
import { readLastUpdatedAtHint } from 'Core-utils/helpers/pageHints';

export type FindingsViewPage = Page<FindingsInsightsDto>;

/** Matches API FINDINGS_COUNT_CAP when exact count times out. */
export const FINDINGS_COUNT_CAP = 5000;

export function isDefaultCappedEstimate(hints: FindingsViewPage['hints'] | undefined): boolean {
    const estimate = hints?.estimate;
    if (estimate !== FINDINGS_COUNT_CAP) {
        return false;
    }
    const count = hints?.count;
    return count === undefined || count === estimate;
}

export function rowFillHasMore(rowCount: number, pageSize: number): boolean {
    return pageSize > 0 && rowCount >= pageSize;
}

function getEffectivePageSize(hints: FindingsViewPage['hints'] | undefined, fallback: number): number {
    return typeof hints?.limit === 'number' && hints.limit > 0 ? hints.limit : fallback;
}

export function resolveHasMore(data: FindingsViewPage | undefined, page: number, pageSize: number): boolean {
    const hints = data?.hints;
    const rowCount = data?.data?.length ?? 0;
    const effectivePageSize = getEffectivePageSize(hints, pageSize);
    const offset = hints?.offset ?? (page - 1) * pageSize;

    if (hints?.countPending || isDefaultCappedEstimate(hints)) {
        return rowFillHasMore(rowCount, effectivePageSize);
    }

    const exactCount = hints?.count;
    if (exactCount !== undefined) {
        return offset + rowCount < exactCount;
    }

    return hints?.hasMore ?? rowFillHasMore(rowCount, effectivePageSize);
}

export function resolvePaginationTotalRows(data: FindingsViewPage | undefined, page: number, pageSize: number): number {
    const hints = data?.hints;
    if (hints?.count !== undefined && !isDefaultCappedEstimate(hints)) {
        return hints.count;
    }

    const rowCount = data?.data?.length ?? 0;
    const effectivePageSize = getEffectivePageSize(hints, pageSize);
    const offset = hints?.offset ?? (page - 1) * pageSize;
    const hasMore = resolveHasMore(data, page, pageSize);

    if (hints?.countPending || isDefaultCappedEstimate(hints)) {
        return offset + rowCount + (hasMore ? effectivePageSize : 0);
    }

    return hints?.count ?? 0;
}

export function shouldShowEstimatedTotal(hints: FindingsViewPage['hints'] | undefined, perfEnabled: boolean): boolean {
    if (hints?.estimate === undefined) {
        return false;
    }
    if (perfEnabled) {
        return isDefaultCappedEstimate(hints);
    }
    return true;
}

export function applyCountResponse(prev: FindingsViewPage | undefined, countPage: FindingsViewPage): FindingsViewPage {
    const target = prev ?? { ...countPage, data: [] };

    if (isDefaultCappedEstimate(countPage.hints)) {
        const rowCount = target.data?.length ?? 0;
        const pageSize = getEffectivePageSize(target.hints, 50);
        const { count: _count, hasMore: _hasMore, ...restHints } = target.hints ?? {};
        return {
            ...target,
            hints: {
                ...restHints,
                estimate: countPage.hints?.estimate,
                hasMore: rowFillHasMore(rowCount, pageSize),
                countPending: false,
                lastUpdatedAt: readLastUpdatedAtHint(countPage.hints) ?? readLastUpdatedAtHint(target.hints),
            },
        };
    }

    const count = countPage.hints?.count;
    const rowCount = target.data?.length ?? 0;
    const offset = target.hints?.offset ?? 0;

    return {
        ...target,
        hints: {
            ...target.hints,
            count,
            estimate: undefined,
            hasMore: count !== undefined ? offset + rowCount < count : countPage.hints?.hasMore,
            countPending: false,
        },
    };
}

/** Normalize raw API hints — never trust capped count/hasMore from the server. */
export function sanitizeApiPageHints(page: FindingsViewPage, pageSizeFallback = 50): FindingsViewPage {
    const rowCount = page.data?.length ?? 0;
    const pageSize = getEffectivePageSize(page.hints, pageSizeFallback);
    const offset = page.hints?.offset ?? 0;

    if (isDefaultCappedEstimate(page.hints)) {
        const { count: _count, ...restHints } = page.hints ?? {};
        return {
            ...page,
            hints: {
                ...restHints,
                estimate: page.hints?.estimate,
                hasMore: rowFillHasMore(rowCount, pageSize),
                countPending: false,
            },
        };
    }

    if (page.hints?.count !== undefined) {
        return {
            ...page,
            hints: {
                ...page.hints,
                hasMore: offset + rowCount < page.hints.count,
                countPending: false,
            },
        };
    }

    if (page.hints?.countPending) {
        return {
            ...page,
            hints: {
                ...page.hints,
                hasMore: rowFillHasMore(rowCount, pageSize),
            },
        };
    }

    return page;
}

export function applyRowsResponse(
    prev: FindingsViewPage | undefined,
    rowsPage: FindingsViewPage,
    pageSizeFallback = 50,
): FindingsViewPage {
    const sanitized = sanitizeApiPageHints(rowsPage, pageSizeFallback);

    if (!prev?.hints) {
        return sanitized;
    }

    const rowCount = sanitized.data?.length ?? 0;
    const pageSize = getEffectivePageSize(sanitized.hints, pageSizeFallback);
    const offset = sanitized.hints?.offset ?? 0;

    if (isDefaultCappedEstimate(prev.hints)) {
        const { count: _count, ...restHints } = sanitized.hints ?? {};
        return {
            ...sanitized,
            hints: {
                ...restHints,
                estimate: prev.hints.estimate,
                hasMore: rowFillHasMore(rowCount, pageSize),
                countPending: false,
            },
        };
    }

    if (prev.hints.count !== undefined) {
        return {
            ...sanitized,
            hints: {
                ...sanitized.hints,
                count: prev.hints.count,
                hasMore: offset + rowCount < prev.hints.count,
                estimate: undefined,
                countPending: false,
            },
        };
    }

    return sanitized;
}

export type GridDisplayState = {
    showSpinner: boolean;
    showMany: boolean;
    displayCount: number | undefined;
    paginationTotalRows: number;
    hasMore: boolean;
};

export function getGridDisplayState(
    data: FindingsViewPage | undefined,
    options: { perfEnabled: boolean; countLoading: boolean; page: number; pageSize: number },
): GridDisplayState {
    return {
        showSpinner: options.perfEnabled && options.countLoading,
        showMany: !options.countLoading && shouldShowEstimatedTotal(data?.hints, options.perfEnabled),
        displayCount: data?.hints?.count,
        paginationTotalRows: resolvePaginationTotalRows(data, options.page, options.pageSize),
        hasMore: resolveHasMore(data, options.page, options.pageSize),
    };
}

/** Single source for FindingsGrid → Table pagination props. */
export function getFindingsGridTablePaginationProps(
    data: FindingsViewPage | undefined,
    options: { perfEnabled: boolean; countLoading: boolean; page: number; pageSize: number },
) {
    const display = getGridDisplayState(data, options);
    return {
        paginationTotalRows: display.paginationTotalRows,
        paginationHasMore: display.hasMore,
        totalCountLoading: display.showSpinner,
        estimated: display.showMany ? data?.hints?.estimate : undefined,
    };
}

export type PerfFetchSnapshot = {
    atMs: number;
    event: 'initial' | 'rows' | 'count';
    data: FindingsViewPage | undefined;
    display: GridDisplayState;
};

export type SimulatePerfFetchOptions = {
    rowsDelayMs: number;
    countDelayMs: number;
    rowsPage: FindingsViewPage;
    countPage: FindingsViewPage;
    perfEnabled?: boolean;
    page?: number;
    pageSize?: number;
};

/** Simulates perf-path parallel rows + count-only fetch with configurable delays. */
export async function simulatePerfGridFetch(
    options: SimulatePerfFetchOptions,
    schedule: (ms: number) => Promise<void> = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
): Promise<{
    snapshots: PerfFetchSnapshot[];
    final: GridDisplayState;
    finalData: FindingsViewPage | undefined;
}> {
    const perfEnabled = options.perfEnabled ?? true;
    const page = options.page ?? 1;
    const pageSize = options.pageSize ?? 50;

    let data: FindingsViewPage | undefined;
    let countLoading = true;
    const snapshots: PerfFetchSnapshot[] = [];

    const record = (atMs: number, event: PerfFetchSnapshot['event']) => {
        snapshots.push({
            atMs,
            event,
            data: data ? { ...data, data: [...data.data], hints: { ...data.hints } } : undefined,
            display: getGridDisplayState(data, { perfEnabled, countLoading, page, pageSize }),
        });
    };

    record(0, 'initial');

    const rowsTask = schedule(options.rowsDelayMs).then(() => {
        data = applyRowsResponse(data, options.rowsPage);
        record(options.rowsDelayMs, 'rows');
    });

    const countTask = schedule(options.countDelayMs).then(() => {
        data = applyCountResponse(data, options.countPage);
        countLoading = false;
        record(options.countDelayMs, 'count');
    });

    await Promise.all([rowsTask, countTask]);

    const final = getGridDisplayState(data, { perfEnabled, countLoading, page, pageSize });
    return { snapshots, final, finalData: data };
}

export function buildRowsPage(rowCount: number, limit = 50, offset = 0): FindingsViewPage {
    const data = Array.from({ length: rowCount }, (_, index) => ({ id: `row-${index}` }) as FindingsInsightsDto);
    return {
        kind: 'findings',
        data,
        error: null,
        hints: {
            limit,
            offset,
            countPending: true,
            hasMore: rowCount >= limit,
        },
    };
}

export function buildExactCountPage(count: number): FindingsViewPage {
    return {
        kind: 'findings',
        data: [],
        error: null,
        hints: {
            count,
            hasMore: false,
        },
    };
}

export function buildCappedCountPage(): FindingsViewPage {
    return {
        kind: 'findings',
        data: [],
        error: null,
        hints: {
            count: FINDINGS_COUNT_CAP,
            estimate: FINDINGS_COUNT_CAP,
            hasMore: true,
        },
    };
}
